import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, ArrowUp, MapPin, RotateCcw, Search, Sparkles } from 'lucide-react';
import { adminApi, aiApi } from '../api';
import { useCapabilities } from '../capabilities';
import { Modal } from './Modal';
import { Button } from './Button';
import { ShelfPreview, clearShelfCache } from './ShelfPreview';
import './FindAsk.css';

const MAX_MATCHES = 8;

const SUGGESTIONS = [
  "What's left to pick today?",
  'Which SKUs in open orders have no location?',
  'Which orders are being picked right now?',
];

// Questions go to the AI; anything that looks like a code is searched instantly first
const looksLikeQuestion = (text) => /\s/.test(text.trim()) || text.includes('?');

// Minimal, safe rendering of the assistant's answer: paragraphs, "- " lists and **bold**
function renderAnswer(text) {
  const bold = (line) => line.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith('**') && part.endsWith('**') ? <strong key={i}>{part.slice(2, -2)}</strong> : part);

  const blocks = [];
  let list = null;
  text.split('\n').forEach((raw, i) => {
    const line = raw.trim();
    const item = /^[-*•]\s+(.*)/.exec(line);
    if (item) {
      if (!list) {
        list = [];
        blocks.push(<ul key={`l${i}`}>{list}</ul>);
      }
      list.push(<li key={i}>{bold(item[1])}</li>);
      return;
    }
    list = null;
    if (line) blocks.push(<p key={i}>{bold(line)}</p>);
  });
  return blocks;
}

// "Find & Ask": instant search over every mapped SKU, bin and shelf, plus (when AI is
// set up) questions answered from live order and storage data.
export function FindAsk({ open, onClose }) {
  const { aiEnabled } = useCapabilities();
  const [query, setQuery] = useState('');
  const [skus, setSkus] = useState(null);
  const [preview, setPreview] = useState(null);
  const [conversation, setConversation] = useState([]);
  const [asking, setAsking] = useState(false);
  const inputRef = useRef(null);
  const transcriptRef = useRef(null);

  // Fresh data each time the panel opens
  useEffect(() => {
    if (!open) return;
    clearShelfCache();
    setSkus(null);
    adminApi.getSkus().then(setSkus).catch(() => setSkus([]));
  }, [open]);

  useEffect(() => {
    transcriptRef.current?.scrollTo({ top: transcriptRef.current.scrollHeight, behavior: 'smooth' });
  }, [conversation, asking]);

  // Exact SKU/bin matches first, then ones that start with the text, then the rest
  const matches = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term || !skus) return [];
    const score = (row) => {
      const values = [row.sku, row.region_label, row.unit_name].map(v => (v || '').toLowerCase());
      if (values.some(v => v === term)) return 0;
      if (values.some(v => v.startsWith(term))) return 1;
      return values.some(v => v.includes(term)) ? 2 : -1;
    };
    return skus
      .map(row => ({ row, rank: score(row) }))
      .filter(({ rank }) => rank >= 0)
      .sort((a, b) => a.rank - b.rank)
      .slice(0, MAX_MATCHES)
      .map(({ row }) => row);
  }, [query, skus]);

  const reset = () => {
    setQuery('');
    setPreview(null);
    setConversation([]);
    inputRef.current?.focus();
  };

  const close = () => {
    reset();
    onClose();
  };

  const ask = async (question) => {
    const text = question.trim();
    if (!text || asking) return;
    const next = [...conversation, { role: 'user', content: text }];
    setConversation(next);
    setQuery('');
    setPreview(null);
    setAsking(true);
    try {
      const { answer, locations } = await aiApi.ask(next.map(({ role, content }) => ({ role, content })));
      setConversation([...next, { role: 'assistant', content: answer, locations }]);
    } catch (error) {
      setConversation([...next, { role: 'assistant', content: error.message, isError: true }]);
    } finally {
      setAsking(false);
      inputRef.current?.focus();
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const text = query.trim();
    if (!text) return;
    if (conversation.length > 0 || (aiEnabled && (looksLikeQuestion(text) || matches.length === 0))) {
      if (aiEnabled) ask(text);
      return;
    }
    if (matches[0]) showLocation(matches[0]);
  };

  const showLocation = (row) => setPreview({ unitId: row.unit_id, regionId: row.region_id, sku: row.sku });

  const chatting = conversation.length > 0;

  return (
    <Modal
      open={open}
      onClose={close}
      size="lg"
      title={aiEnabled ? 'Find & Ask' : 'Find stock'}
      description={aiEnabled
        ? 'Type a SKU to see where it is, or ask a question about orders and stock.'
        : 'Type a SKU, bin or shelf to see where it is.'}
    >
      <form className="findask-input" onSubmit={handleSubmit}>
        {aiEnabled && chatting ? <Sparkles size={18} aria-hidden="true" /> : <Search size={18} aria-hidden="true" />}
        <input
          ref={inputRef}
          className="input"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setPreview(null);
          }}
          placeholder={chatting ? 'Ask a follow-up…' : aiEnabled ? 'STK-CAT, "A3", or "what\'s left to pick?"' : 'STK-CAT, A3, Shelf B…'}
          aria-label={aiEnabled ? 'Search or ask a question' : 'Search SKUs, bins and shelves'}
          autoComplete="off"
          data-autofocus
        />
        {aiEnabled && query.trim() && (
          <button type="submit" className="findask-send" aria-label="Send" disabled={asking}>
            <ArrowUp size={18} />
          </button>
        )}
      </form>

      <div className="findask-body">
        {preview ? (
          <div className="findask-preview">
            <button type="button" className="findask-back" onClick={() => setPreview(null)}>
              <ArrowLeft size={15} aria-hidden="true" /> {chatting ? 'Back to answer' : 'Back to results'}
            </button>
            <ShelfPreview unitId={preview.unitId} regionId={preview.regionId} sku={preview.sku} />
          </div>
        ) : chatting ? (
          <div className="findask-transcript" ref={transcriptRef} aria-live="polite">
            {conversation.map((turn, i) => (
              <div key={i} className={`findask-turn findask-turn-${turn.role} ${turn.isError ? 'is-error' : ''}`}>
                {turn.role === 'assistant' ? (
                  <>
                    <div className="findask-answer">{renderAnswer(turn.content)}</div>
                    {turn.locations?.length > 0 && (
                      <div className="findask-locations">
                        {turn.locations.slice(0, 12).map(loc => (
                          <button
                            key={`${loc.region_id}-${loc.sku}`}
                            type="button"
                            className="findask-location"
                            onClick={() => showLocation({ unit_id: loc.unit_id, region_id: loc.region_id, sku: loc.sku })}
                          >
                            <MapPin size={13} aria-hidden="true" />
                            <span className="mono">{loc.sku}</span>
                            <span className="bin-tag">{loc.bin || 'Bin'}</span>
                            <span className="muted">{loc.storage_unit}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <p>{turn.content}</p>
                )}
              </div>
            ))}
            {asking && (
              <div className="findask-turn findask-turn-assistant findask-thinking" role="status">
                <span /><span /><span />
                <span className="sr-only">Looking it up…</span>
              </div>
            )}
          </div>
        ) : query.trim() ? (
          <ul className="findask-results">
            {skus === null && <li className="findask-muted">Loading…</li>}
            {matches.map(row => (
              <li key={`${row.region_id}-${row.sku}`}>
                <button type="button" className="findask-result" onClick={() => showLocation(row)}>
                  <span className="mono findask-result-sku">{row.sku}</span>
                  <span className="bin-tag">{row.region_label || 'Bin'}</span>
                  <span className="findask-result-unit">{row.unit_name}</span>
                </button>
              </li>
            ))}
            {skus !== null && matches.length === 0 && !aiEnabled && (
              <li className="findask-muted">No SKU, bin or shelf matches “{query.trim()}”.</li>
            )}
            {aiEnabled && (
              <li>
                <button type="button" className="findask-result findask-ask" onClick={() => ask(query)}>
                  <Sparkles size={16} aria-hidden="true" />
                  <span>Ask AI: “{query.trim()}”</span>
                </button>
              </li>
            )}
          </ul>
        ) : aiEnabled ? (
          <div className="findask-suggestions">
            <p className="findask-muted">Try asking</p>
            {SUGGESTIONS.map(text => (
              <button key={text} type="button" className="findask-suggestion" onClick={() => ask(text)}>
                <Sparkles size={15} aria-hidden="true" />
                {text}
              </button>
            ))}
          </div>
        ) : (
          <p className="findask-muted">Start typing to see where it's stored.</p>
        )}
      </div>

      {chatting && (
        <div className="findask-footer">
          <Button size="sm" variant="ghost" icon={RotateCcw} onClick={reset}>New search</Button>
          <span className="findask-muted">Answers come from your live orders and shelves.</span>
        </div>
      )}
    </Modal>
  );
}
